import { FreightModeBaseTypes } from "constants/";

type TrackingTypeOption = { label: string; value: string };

type GetAllowedTrackingTypesOptions = {
  excludeBookingReference?: boolean;
  includeHbl?: boolean;
};

const ALWAYS_SHOWN_OPTIONS = ["other", "bookingReference"];

// Only used when the shipment has no freight mode type, e.g. drafts without a booked quotation.
const BASE_TYPE_BY_FREIGHT_MODE_NAME: Record<string, string> = {
  "Ocean freight": FreightModeBaseTypes.OCEAN,
  "Road freight": FreightModeBaseTypes.ROAD,
  Parcel: FreightModeBaseTypes.PARCEL,
  "Rail freight": FreightModeBaseTypes.RAIL,
  "Regular air freight": FreightModeBaseTypes.AIR,
  "On board courier (OBC)": FreightModeBaseTypes.AIR,
  "Next flight out (NFO)": FreightModeBaseTypes.AIR,
  "Air charter": FreightModeBaseTypes.AIR,
};

function resolveBaseType(freightMode: string, freightModeType: string | undefined): string | undefined {
  if (freightModeType === "sea") return FreightModeBaseTypes.OCEAN;
  if (freightModeType) return freightModeType;
  return BASE_TYPE_BY_FREIGHT_MODE_NAME[freightMode];
}

function getAllowedValuesForBaseType(
  baseType: string | undefined,
  typeOptions: TrackingTypeOption[],
  includeHbl?: boolean,
): string[] {
  switch (baseType) {
    case FreightModeBaseTypes.OCEAN: {
      const allowed = [...ALWAYS_SHOWN_OPTIONS, "primary", "container_no", "mbol"];
      if (includeHbl) {
        allowed.push("hbl");
      }
      return allowed;
    }
    case FreightModeBaseTypes.ROAD:
      return [...ALWAYS_SHOWN_OPTIONS, "primary", "truck"];
    case FreightModeBaseTypes.PARCEL:
      return [...ALWAYS_SHOWN_OPTIONS, "primary"];
    case FreightModeBaseTypes.RAIL:
      return [...ALWAYS_SHOWN_OPTIONS, "rail_car", "primary", "container_no"];
    case FreightModeBaseTypes.AIR:
      return [...ALWAYS_SHOWN_OPTIONS, "primary", "mawb"];
    default:
      return typeOptions.map((option) => option.value);
  }
}

/**
 * Resolve the reference types a shipment may use, based on its base freight mode type.
 * Custom freight modes carry their own name but inherit the base type, so the type drives the list.
 */
export default function getAllowedTrackingTypes(
  freightMode: string,
  freightModeType: string | undefined,
  typeOptions: TrackingTypeOption[],
  options?: GetAllowedTrackingTypesOptions,
): TrackingTypeOption[] {
  const allowedValues = getAllowedValuesForBaseType(
    resolveBaseType(freightMode, freightModeType),
    typeOptions,
    options?.includeHbl,
  );

  let result = typeOptions.filter((option) => allowedValues.includes(option.value));

  if (options?.excludeBookingReference) {
    result = result.filter((option) => option.value !== "bookingReference");
  }

  return result;
}
